/**
 * The review queue's contract, narrowed from `unknown` at the boundary.
 *
 * The server sends one flat row per khutbah-and-language with counts;
 * `ReviewRow.d.ts` describes the same row as nested state. The mapping lives
 * here so a component never has to decide, for instance, whether a language
 * counts as reviewed — that is derived once, from `reviewedCount` against
 * `passages`, and every screen agrees because there is one answer.
 */

export class ResponseShapeError extends Error {
  constructor(what: string) {
    super(`The server sent something this version cannot read: ${what}`);
    this.name = 'ResponseShapeError';
  }
}

export type QueueTab = 'unreviewed' | 'reviewed' | 'rejected' | 'stale';
export type QueueSort = 'oldest-live' | 'newest' | 'most-flags' | 'khutbah';

/** From RejectDialog.d.ts, and the server's own fixed list. */
export const REJECT_CAUSES = [
  'omission',
  'addition',
  'meaning',
  'term',
  'unreadable',
  'wrong-passage',
  'other',
] as const;
export type RejectCause = (typeof REJECT_CAUSES)[number];

/** What a reviewer would call each cause. The codes are not for reading. */
export const CAUSE_LABELS: Record<RejectCause, string> = {
  omission: 'Something is missing',
  addition: 'Something was added',
  meaning: 'The meaning is changed',
  term: 'Wrong term',
  unreadable: 'Unreadable',
  'wrong-passage': 'Attached to the wrong passage',
  other: 'Something else',
};

export interface QueueSummary {
  readonly liveUnreviewed: number;
  readonly maxLiveHours: number;
  readonly overdue: number;
  readonly overdueAfterHours: number;
  readonly withFlags: number;
  readonly stale: number;
  /** False everywhere today: nothing runs the faithfulness check. */
  readonly faithfulnessCheckOn: boolean;
}

export interface ReviewRowData {
  readonly id: string;
  readonly khutbahId: string;
  readonly titleAr: string | null;
  readonly friday: string;
  readonly institutionKey: string;
  readonly institutionNameAr: string | null;
  readonly sourceKind: 'publisher' | 'user';
  readonly language: string;
  readonly languageName: string;
  readonly origin: 'ai' | 'human' | 'publisher';
  readonly declaredModel: string | null;
  readonly producedByName: string | null;
  readonly passages: number;
  readonly flagCount: number;
  readonly staleCount: number;
  readonly liveSince: string | null;
  readonly reviewerName: string | null;
  readonly reviewedAt: string | null;
  readonly rejectedAt: string | null;
  readonly rejectionNote: string | null;
  readonly rejectionCauses: readonly string[];
  readonly withdrawn: boolean;
  /** Derived by the server so no two screens can disagree. */
  readonly reviewed: boolean;
  readonly checked: boolean;
  readonly stale: boolean;
  readonly rejected: boolean;
}

export interface QueueView {
  readonly summary: QueueSummary;
  readonly rows: readonly ReviewRowData[];
}

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ResponseShapeError(`${where} is not an object`);
  }
  return value as Record<string, unknown>;
}

function num(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function text(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parseQueueView(value: unknown): QueueView {
  const source = asRecord(value, 'queue');
  const summary = asRecord(source.summary, 'queue.summary');
  const rows = source.rows;
  if (!Array.isArray(rows)) {
    throw new ResponseShapeError('queue.rows is not an array');
  }

  return {
    summary: {
      liveUnreviewed: num(summary, 'liveUnreviewed'),
      maxLiveHours: num(summary, 'maxLiveHours'),
      overdue: num(summary, 'overdue'),
      // 7 days, matching the server, rather than 0 — which would mark every
      // row overdue the moment the field went missing.
      overdueAfterHours: num(summary, 'overdueAfterHours') || 168,
      withFlags: num(summary, 'withFlags'),
      stale: num(summary, 'stale'),
      faithfulnessCheckOn: summary.faithfulnessCheckOn === true,
    },
    rows: rows.map((entry, index) => {
      const row = asRecord(entry, `rows[${index}]`);
      const id = row.id;
      if (typeof id !== 'string') {
        throw new ResponseShapeError(`rows[${index}] has no id`);
      }
      return {
        id,
        khutbahId: text(row, 'khutbahId') ?? '',
        titleAr: text(row, 'titleAr'),
        friday: text(row, 'friday') ?? '',
        institutionKey: text(row, 'institutionKey') ?? '',
        institutionNameAr: text(row, 'institutionNameAr'),
        sourceKind: row.sourceKind === 'user' ? 'user' : 'publisher',
        language: text(row, 'language') ?? '',
        languageName: text(row, 'languageName') ?? text(row, 'language') ?? '',
        origin:
          row.origin === 'ai' || row.origin === 'publisher' ? row.origin : 'human',
        declaredModel: text(row, 'declaredModel'),
        producedByName: text(row, 'producedByName'),
        passages: num(row, 'passages'),
        flagCount: num(row, 'flagCount'),
        staleCount: num(row, 'staleCount'),
        liveSince: text(row, 'liveSince'),
        reviewerName: text(row, 'reviewerName'),
        reviewedAt: text(row, 'reviewedAt'),
        rejectedAt: text(row, 'rejectedAt'),
        rejectionNote: text(row, 'rejectionNote'),
        rejectionCauses: Array.isArray(row.rejectionCauses)
          ? row.rejectionCauses.filter((c): c is string => typeof c === 'string')
          : [],
        withdrawn: row.withdrawn === true,
        reviewed: row.reviewed === true,
        checked: row.checked === true,
        stale: row.stale === true,
        rejected: row.rejected === true,
      };
    }),
  };
}

/**
 * How long readers have had it, in whole hours.
 *
 * Null when it was never published — which is not zero hours. A translation
 * nobody can see has no "live" age at all, and showing "0h live" would
 * invite a reviewer to treat it as urgent when it is the opposite.
 */
export function liveHours(row: ReviewRowData, now: number = Date.now()): number | null {
  if (row.liveSince === null) {
    return null;
  }
  const since = Date.parse(row.liveSince);
  if (Number.isNaN(since)) {
    return null;
  }
  return Math.max(0, Math.floor((now - since) / 3_600_000));
}

/** "9 days" reads better than "216h" past the first day. */
export function formatAge(hours: number): string {
  if (hours < 1) {
    return 'under an hour';
  }
  if (hours < 48) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${Math.floor(hours / 24)} days`;
}

export function isOverdue(row: ReviewRowData, summary: QueueSummary): boolean {
  const hours = liveHours(row);
  return hours !== null && !row.reviewed && hours >= summary.overdueAfterHours;
}

/**
 * The sentence the screen leads with.
 *
 * Deliberately about readers rather than about work: the queue is not a list
 * of things waiting to go out, it is text people are already reading that
 * nobody has checked.
 */
export function headline(summary: QueueSummary): string {
  if (summary.liveUnreviewed === 0) {
    return 'Nothing live is unreviewed.';
  }
  const one = summary.liveUnreviewed === 1;
  const what = `${summary.liveUnreviewed} translation${one ? ' is' : 's are'} live and unreviewed`;
  return summary.maxLiveHours > 0
    // "them" for one translation is the kind of slip that makes a careful
    // screen look careless, and this is the first line a reviewer reads.
    ? `${what} · readers have seen ${one ? 'it' : 'them'} for up to ${formatAge(summary.maxLiveHours)}`
    : `${what}.`;
}
